// ===========================================
// SCAN A PO
// ===========================================
// Photograph or upload a distributor's purchase order; a vision model reads it
// (with the maker's own catalog in the prompt, so SKUs come back matched) and
// prefills the full PO — distributor, PO#, products with lot codes, logistics —
// for a quick check before it lands in Open Orders.

import { useRef, useState } from 'react';
import { X, Camera, Upload, Check, Loader2, Plus } from 'lucide-react';
import { wholesaleApi } from '../services/api';

type MenuItem = { id: string; name: string; priceCents: number; description?: string };
type Customer = { id: string; name: string; isDistributor?: boolean | null };
type Line = { menuItemId: string; quantity: string; unitDollars: string; lotCode: string; rawName?: string };

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
// "SKU 0001" in the description → "0001 · Apple Juice 12pk 8oz", so the dropdown
// matches the code printed on the distributor's PO.
export const skuLabel = (m: { name: string; description?: string }) => {
  const code = /(\d{3,6})/.exec(m.description || '')?.[1];
  return code ? `${code} · ${m.name}` : m.name;
};

export default function ScanPOModal({ customers, menuItems, onClose, onCreated, onError }: {
  customers: Customer[]; menuItems: MenuItem[];
  onClose: () => void; onCreated: (msg: string) => void; onError: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [parsed, setParsed] = useState(false);
  const [poNumber, setPoNumber] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [custQuery, setCustQuery] = useState('');
  const [addingCust, setAddingCust] = useState(false);
  const [newCust, setNewCust] = useState({ name: '', email: '' });
  const [date, setDate] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [shipFrom, setShipFrom] = useState('');
  const [warehouse, setWarehouse] = useState('');
  const [shipDate, setShipDate] = useState('');
  const [shipTo, setShipTo] = useState('');
  const [saving, setSaving] = useState(false);
  const cameraRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<HTMLInputElement>(null);

  // Only distributors belong here — direct-delivery customers are a different
  // universe (his Sep 4 feedback). Fall back to everyone until flags are set.
  const flagged = customers.filter((c) => c.isDistributor);
  const distributors = (flagged.length ? flagged : customers).slice().sort((a, b) => a.name.localeCompare(b.name));

  const pickCustomer = (typed: string) => {
    setCustQuery(typed);
    const t = norm(typed);
    const exact = distributors.find((c) => norm(c.name) === t);
    setCustomerId(exact?.id || '');
  };

  const matchCustomer = (name?: string) => {
    if (!name) return null;
    const n = norm(name);
    return distributors.find((c) => { const cn = norm(c.name); return cn === n || cn.includes(n) || n.includes(cn.split(' ')[0]); }) || null;
  };

  // Scored matcher — a wrong guess is worse than an empty field, so anything
  // ambiguous scores itself out. The unit price is the strongest signal when
  // the PO text omits the size (KeHE prints "JUICE APPLE HBSC FIZZ" for both
  // the 8oz and the $62 750ml — only the price tells them apart).
  const matchProduct = (pname?: string, sku?: string, matched?: string, unitCents?: number | null) => {
    const n = norm(pname || '');
    const matchedNorm = norm(matched || '');
    let best: { id: string; score: number } | null = null;
    for (const mi of menuItems) {
      const mn = norm(mi.name);
      const skuHit = !!(sku && norm(mi.description || '').includes(norm(String(sku))));
      const words = mn.split(' ').filter(Boolean);
      const hits = n ? words.filter((w) => n.includes(w)).length : 0;
      // No name/SKU evidence at all → this candidate is out entirely. Price can
      // break a tie between plausible names, but must never invent a match —
      // a granola bar that happens to cost $19.20 is not apple juice.
      if (!skuHit && hits === 0 && mn !== matchedNorm) continue;
      let score = 0;
      if (matchedNorm && mn === matchedNorm) score += 300;
      if (skuHit) score += 300;
      if (n) {
        if (mn === n) score += 1000;
        else {
          score += hits === words.length ? 500 : hits * 10;
          if (/sparkling/.test(n) !== /sparkling/.test(mn)) score -= 400;
          // size words are only evidence when the PO text actually has them
          if (/750/.test(n) && !/750/.test(mn)) score -= 300;
          if (!/750/.test(n) && /750/.test(mn)) score -= 100;
          if (/16 ?ct/.test(n) !== /16 ?ct/.test(mn)) score -= 100;
        }
      }
      if (unitCents && mi.priceCents) {
        const ratio = unitCents / mi.priceCents;
        // the bonus needs real name evidence (≥2 words / SKU / exact) — one
        // shared word like "apple" plus a plausible price is not a match
        const nameStrong = skuHit || mn === matchedNorm || mn === n || hits >= 2;
        if (nameStrong && ratio >= 0.7 && ratio <= 1.4) score += 400;  // price agrees
        else if (ratio < 0.5 || ratio > 2) score -= 400;               // price says no
      }
      if (!best || score > best.score) best = { id: mi.id, score };
    }
    return best && best.score >= 100 ? best.id : '';
  };

  const handleFile = async (file: File) => {
    setBusy(true); setNote('Reading the PO…');
    try {
      const res = await wholesaleApi.parseOrder(file);
      const p = res.data?.data?.parsed;
      setParsed(true);
      if (!p) { setNote("Couldn't read it automatically — fill in the details below."); setLines([{ menuItemId: '', quantity: '', unitDollars: '', lotCode: '' }]); return; }
      setPoNumber(p.poNumber || '');
      const c = matchCustomer(p.distributorName);
      setCustomerId(c?.id || '');
      setCustQuery(c?.name || p.distributorName || '');
      setDate(p.orderDate || '');
      setShipFrom(p.shipFrom || '');
      setShipDate(p.expectedShipDate || '');
      setShipTo(p.shipTo || '');
      const parsedLines: Line[] = (p.lineItems || []).map((it: any) => ({
        menuItemId: matchProduct(it.productName, it.sku, it.matchedProduct, it.unitPriceCents),
        quantity: it.quantity != null ? String(it.quantity) : '',
        unitDollars: typeof it.unitPriceCents === 'number' ? (it.unitPriceCents / 100).toFixed(2) : '',
        lotCode: '',
        rawName: [it.productName, it.sku ? `#${it.sku}` : ''].filter(Boolean).join(' '),
      }));
      setLines(parsedLines.length ? parsedLines : [{ menuItemId: '', quantity: '', unitDollars: '', lotCode: '' }]);
      setNote('Read from the photo — check the details before saving.');
    } catch {
      setNote('The upload failed — please try again.');
    } finally { setBusy(false); }
  };

  const addDistributor = async () => {
    if (!newCust.name.trim()) return;
    try {
      const res = await wholesaleApi.createCustomer({ name: newCust.name.trim(), email: newCust.email.trim() || undefined });
      const id = res.data?.data?.id;
      if (id) {
        await wholesaleApi.updateCustomer(id, { isDistributor: true, ...(newCust.email.trim() ? { invoiceEmail: newCust.email.trim() } : {}) });
        customers.push({ id, name: newCust.name.trim(), isDistributor: true });
        setCustomerId(id); setCustQuery(newCust.name.trim());
        setAddingCust(false); setNewCust({ name: '', email: '' });
      }
    } catch (e: any) {
      onError(e?.response?.data?.error?.message || "Couldn't add the distributor.");
    }
  };

  const create = async () => {
    if (!customerId) return onError('Pick the distributor first — type to search, or add a new one.');
    const items = lines
      .filter((l) => l.menuItemId && parseFloat(l.quantity) > 0)
      .map((l) => ({ menuItemId: l.menuItemId, quantity: parseFloat(l.quantity), unitPriceCents: l.unitDollars ? Math.round(parseFloat(l.unitDollars) * 100) : undefined, lotCode: l.lotCode || undefined }));
    if (items.length === 0) return onError('Add at least one product with a quantity.');
    setSaving(true);
    try {
      const res = await wholesaleApi.createOrder({ customerId, poNumber: poNumber || undefined, fulfillmentDate: date || undefined, items: items.map(({ lotCode, ...rest }) => rest) });
      const orderId = res.data?.data?.id;
      // second pass: logistics + per-line lot codes (the create route is minimal on purpose)
      if (orderId && (shipFrom || warehouse || shipDate || items.some((i) => i.lotCode))) {
        await wholesaleApi.setStage(orderId, {
          ...(shipFrom ? { shipFrom } : {}), ...(warehouse ? { warehouse } : {}), ...(shipDate ? { shipDate } : {}),
          ...(items.some((i) => i.lotCode) ? { items } : {}),
        });
      }
      onCreated(`PO ${poNumber || ''} added to Open Orders.`.replace('  ', ' '));
    } catch (e: any) {
      onError(e?.response?.data?.error?.message || "Couldn't create the PO.");
    } finally { setSaving(false); }
  };

  const input = 'w-full text-sm rounded-lg border border-slate-200 px-2.5 py-1.5 focus:ring-2 focus:ring-primary-400 focus:border-primary-400';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 sticky top-0 bg-white">
          <h3 className="font-semibold text-slate-900">Scan a PO</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {!parsed ? (
            <>
              <p className="text-sm text-slate-500">Take a photo of the distributor's purchase order, or upload one. The details fill themselves in.</p>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => cameraRef.current?.click()} disabled={busy} className="card p-5 flex flex-col items-center gap-2 hover:bg-slate-50">
                  {busy ? <Loader2 className="w-6 h-6 animate-spin text-primary-500" /> : <Camera className="w-6 h-6 text-primary-500" />}
                  <span className="text-sm font-medium">Take photo</span>
                </button>
                <button onClick={() => uploadRef.current?.click()} disabled={busy} className="card p-5 flex flex-col items-center gap-2 hover:bg-slate-50">
                  <Upload className="w-6 h-6 text-primary-500" />
                  <span className="text-sm font-medium">Upload file</span>
                </button>
              </div>
              {note && <p className="text-sm text-slate-500 text-center">{note}</p>}
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
              <input ref={uploadRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
            </>
          ) : (
            <>
              {note && <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">{note}</p>}
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-slate-500">PO #<input className={input} value={poNumber} onChange={(e) => setPoNumber(e.target.value)} /></label>
                <label className="text-xs text-slate-500">Date received<input type="date" className={input} value={date} onChange={(e) => setDate(e.target.value)} /></label>
                <div className="col-span-2">
                  <label className="text-xs text-slate-500">Distributor — type to search
                    <input list="scan-distributors" className={input} value={custQuery} onChange={(e) => pickCustomer(e.target.value)}
                      placeholder="Start typing…" />
                    <datalist id="scan-distributors">
                      {distributors.map((c) => <option key={c.id} value={c.name} />)}
                    </datalist>
                  </label>
                  <div className="flex items-center gap-2 mt-1">
                    {customerId
                      ? <span className="text-[11px] text-emerald-700">✓ {distributors.find((c) => c.id === customerId)?.name}</span>
                      : custQuery ? <span className="text-[11px] text-amber-700">No distributor by that name yet.</span> : null}
                    {!addingCust && <button onClick={() => { setAddingCust(true); setNewCust({ name: custQuery, email: '' }); }} className="text-[11px] text-primary-600 font-medium flex items-center gap-0.5"><Plus className="w-3 h-3" /> New distributor</button>}
                  </div>
                  {addingCust && (
                    <div className="mt-2 rounded-xl bg-slate-50 p-3 space-y-2">
                      <input className={input} placeholder="Distributor name" value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} />
                      <input className={input} placeholder="AP / invoicing email (optional — add the rest in Customers)" value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} />
                      <div className="flex gap-2">
                        <button onClick={addDistributor} disabled={!newCust.name.trim()} className="btn btn-sm btn-primary">Add</button>
                        <button onClick={() => setAddingCust(false)} className="btn btn-sm btn-ghost">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-xs font-medium text-slate-500">Products</p>
                {lines.map((l, i) => (
                  <div key={i} className="rounded-xl bg-slate-50 p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      {/* what the scanner actually read, so a wrong match is visible at a glance */}
                      <p className="text-[11px] text-slate-400 min-w-0 truncate" title={l.rawName || undefined}>
                        {l.rawName ? <>on the PO: <span className="text-slate-500 font-medium">{l.rawName}</span></> : 'added by hand'}
                        {l.rawName && !l.menuItemId && <span className="text-amber-600"> — no confident match, pick below</span>}
                      </p>
                      <button onClick={() => setLines(lines.filter((_, j) => j !== i))} className="text-slate-300 hover:text-rose-500 shrink-0"><X className="w-4 h-4" /></button>
                    </div>
                    <select className={input} value={l.menuItemId} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, menuItemId: e.target.value } : x))}>
                      <option value="">Pick the SKU…</option>
                      {menuItems.map((m) => <option key={m.id} value={m.id}>{skuLabel(m)}</option>)}
                    </select>
                    <div className="grid grid-cols-3 gap-2">
                      <label className="text-[11px] text-slate-500">Cases
                        <input className={input + ' text-right'} placeholder="0" value={l.quantity} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))} />
                      </label>
                      <label className="text-[11px] text-slate-500">$ / case
                        <input className={input + ' text-right'} placeholder="0.00" value={l.unitDollars} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, unitDollars: e.target.value } : x))} />
                      </label>
                      <label className="text-[11px] text-slate-500">Lot code
                        <input className={input} placeholder="—" value={l.lotCode} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, lotCode: e.target.value } : x))} />
                      </label>
                    </div>
                  </div>
                ))}
                <button onClick={() => setLines([...lines, { menuItemId: '', quantity: '', unitDollars: '', lotCode: '' }])} className="text-sm text-primary-600 font-medium">+ Add product</button>
              </div>
              {/* the rest of the record, up front — no pencil-editing afterwards */}
              <div className="grid grid-cols-3 gap-3">
                <label className="text-xs text-slate-500">Ship from<input className={input} placeholder="VT / LA…" value={shipFrom} onChange={(e) => setShipFrom(e.target.value)} /></label>
                <label className="text-xs text-slate-500">Warehouse<input className={input} value={warehouse} onChange={(e) => setWarehouse(e.target.value)} /></label>
                <label className="text-xs text-slate-500">Expected ship<input type="date" className={input} value={shipDate} onChange={(e) => setShipDate(e.target.value)} /></label>
              </div>
              {shipTo && (
                <p className="text-[11px] text-slate-500 bg-slate-50 rounded-lg px-2.5 py-1.5">
                  PO ships to: <span className="font-medium text-slate-700">{shipTo}</span>
                  <span className="text-slate-400"> — delivery addresses live on the customer's card; check it matches.</span>
                </p>
              )}
              <div className="flex gap-2 pt-1">
                <button onClick={create} disabled={saving} className="btn btn-primary flex items-center gap-1.5">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Create PO</button>
                <button onClick={() => { setParsed(false); setNote(''); }} className="btn btn-secondary">Rescan</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
